import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createEngine, {
  DiagramModel,
} from "@projectstorm/react-diagrams";
import { CanvasWidget } from "@projectstorm/react-canvas-core";
import { SolutionType } from "../store/slices/settingsSlice";
import SolutionFlowNodeFactory from "./flowNodes/SolutionFlowNodeFactory";
import SolutionFlowNodeModel from "./flowNodes/SolutionFlowNodeModel";

interface SolutionFlowOverviewProps {
  solution?: SolutionType;
  activeStepIndex?: number;
  focusedInputGroupId?: string | null;
  focusedInputNodeKey?: string | null;
  onNodeClick?: (selection: {
    nodeKind: "solution" | "step" | "group" | "input";
    action?: "edit";
    stepIndex?: number;
    groupId?: string;
    inputIndex?: number;
    inputFocusKey?: string;
    clientX?: number;
    clientY?: number;
  }) => void;
}


const SolutionFlowOverview: React.FC<SolutionFlowOverviewProps> = ({
  solution,
  activeStepIndex,
  focusedInputGroupId,
  focusedInputNodeKey,
  onNodeClick,
}) => {
  const steps = solution?.ComponentConfig?.steps || [];
  const diagramContainerRef = useRef<HTMLDivElement | null>(null);
  const onNodeClickRef = useRef(onNodeClick);
  const diagramNodeClickRef = useRef<(selection: any) => void>(() => {});
  const previousStructureSignatureRef = useRef<string | null>(null);
  const pendingInitialFitRef = useRef(true);
  const animationFrameRef = useRef<number | null>(null);
  const focusTargetRef = useRef<
    Record<string, { x: number; y: number; width: number; height: number }>
  >({});
  const [isFullscreen, setIsFullscreen] = useState(false);

  const FOCUS_VIEW_MARGIN = 28;

  const LAYOUT = {
    baseX: 60,
    baseY: 220,
    stepToGroupX: 360,
    groupToInputX: 300,
    maxInputsPerRow: 5,
    inputHorizontalGap: 280,
    inputVerticalGap: 150,
    groupRowHeight: 120,
    groupVerticalGap: 210,
    groupBottomGap: 90,
    minStepSpan: 220,
    stepVerticalGap: 120,
  };

  const engine = useMemo(() => {
    const diagramEngine = createEngine();
    diagramEngine.getNodeFactories().registerFactory(
      new SolutionFlowNodeFactory((selection: any) => {
        diagramNodeClickRef.current(selection);
      }) as any
    );

    return diagramEngine;
  }, []);

  const refreshLinkGeometry = useCallback((diagramEngine: any) => {
    const currentModel = diagramEngine?.getModel?.() || diagramEngine?.model;

    (currentModel?.getNodes?.() || []).forEach((node: any) => {
      Object.values(node.getPorts?.() || {}).forEach((port: any) => {
        if (port?.reportedPosition) {
          port.reportPosition?.();
        }
      });
    });

    const linkFactories = diagramEngine?.getLinkFactories?.()?.getFactories?.() || {};
    Object.values(linkFactories).forEach((factory: any) => {
      if (Array.isArray(factory?.canvasMatrix)) {
        factory.canvasMatrix = [];
      }
      if (Array.isArray(factory?.routingMatrix)) {
        factory.routingMatrix = [];
      }
      factory?.calculateRoutingMatrix?.();
    });
  }, []);

  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  const structureSignature = useMemo(() => {
    return JSON.stringify(
      steps.map((step: any, stepIndex: number) => ({
        step: step?.id || step?.UUID || step?.name || stepIndex,
        groups: (step?.inputGroups || []).map((group: any, groupIndex: number) => ({
          group: group?.id || group?.UUID || group?.name || groupIndex,
          inputs: (group?.inputs || []).map((input: any, inputIndex: number) =>
            input?.UUID ||
            (input?.ComponentUuid && input?.Field
              ? `${input.ComponentUuid}:${input.Field}`
              : null) ||
            input?.Name ||
            input?.Label ||
            inputIndex
          ),
        })),
      }))
    );
  }, [steps]);

  const model = useMemo(() => {
    focusTargetRef.current = {};

    const model = new DiagramModel();

    const solutionNode = new SolutionFlowNodeModel({
      nodeKind: "solution",
      title: solution?.Label || "Solution",
      subtitle: solution?.Reference || "",
      color: "rgb(42, 112, 181)",
      flowSelection: { nodeKind: "solution" as const },
    });
    solutionNode.setPosition(60, 40);
    const solutionOut = solutionNode.getPort("steps-out") as any;
    model.addNode(solutionNode);

    const stepPorts: Array<{ inPort: any; outPort: any; groupsOutPort: any }> = [];
    const inputLookup = new Map<string, { inPort: any; outPort: any }>();
    const visibilitySourcesByField = new Map<
      string,
      Array<{ y: number; outPort: any }>
    >();
    const visibilityDependents: Array<{
      y: number;
      visibilityField: string;
      inPort: any;
    }> = [];

    const getInputRowCount = (group: any) => {
      return Math.max(
        1,
        Math.ceil((group?.inputs?.length || 0) / LAYOUT.maxInputsPerRow)
      );
    };

    const getGroupVisualHeight = (group: any) => {
      return (
        LAYOUT.groupRowHeight +
        (getInputRowCount(group) - 1) * LAYOUT.inputVerticalGap
      );
    };

    const getGroupStackSpan = (group: any) => {
      return Math.max(
        LAYOUT.groupVerticalGap,
        getGroupVisualHeight(group) + LAYOUT.groupBottomGap
      );
    };

    const getStepRequiredSpan = (step: any) => {
      const groups = step?.inputGroups || [];
      if (groups.length === 0) {
        return LAYOUT.minStepSpan;
      }

      let groupOffsetY = 0;
      let groupsBlockHeight = 0;
      groups.forEach((group: any) => {
        groupsBlockHeight = Math.max(
          groupsBlockHeight,
          groupOffsetY + getGroupVisualHeight(group)
        );
        groupOffsetY += getGroupStackSpan(group);
      });

      return Math.max(LAYOUT.minStepSpan, groupsBlockHeight + 40);
    };

    let nextStepY = LAYOUT.baseY;

    const getInputFocusKey = (
      stepIndex: number,
      groupId: string,
      input: any,
      index: number
    ) => {
      if (!groupId) return null;
      const identity =
        input?.UUID ||
        (input?.ComponentUuid && input?.Field
          ? `${input.ComponentUuid}:${input.Field}`
          : null) ||
        (input?.ComponentUuid && input?.Name
          ? `${input.ComponentUuid}:${input.Name}`
          : null) ||
        (input?.ComponentUuid && input?.Label
          ? `${input.ComponentUuid}:${input.Label}`
          : null) ||
        `${index}`;
      return `step:${stepIndex}|group:${groupId}|input:${identity}`;
    };

    steps.forEach((step: any, stepIdx: number) => {
      const stepRequiredSpan = getStepRequiredSpan(step);
      const stepHasVisibilityDependency = !!step.VisibilityField;
      const stepNode = new SolutionFlowNodeModel({
        nodeKind: "step",
        title: `Step ${stepIdx + 1}: ${step.name || "Untitled"}`,
        subtitle: step.description || "",
        meta: `${step.inputGroups?.length || 0} group(s)`,
        color:
          stepIdx === activeStepIndex ? "rgb(108, 92, 231)" : "rgb(72, 71, 95)",
        flowSelection: {
          nodeKind: "step" as const,
          stepIndex: stepIdx,
        },
      });
      const stepX = LAYOUT.baseX;
      const stepY = nextStepY;
      stepNode.setPosition(stepX, stepY);
      const stepIn = stepNode.getPort("step-in") as any;
      const stepOut = stepNode.getPort("step-out") as any;
      const groupsOutPort = stepNode.getPort("groups-out") as any;
      const stepVisibilityIn = stepNode.getPort("visibility-in") as any;
      model.addNode(stepNode);
      stepPorts.push({ inPort: stepIn, outPort: stepOut, groupsOutPort });

      focusTargetRef.current[`step:${stepIdx}`] = {
        x: stepX,
        y: stepY,
        width: 260,
        height: 120,
      };

      if (step.VisibilityField) {
        visibilityDependents.push({
          y: stepY,
          visibilityField: String(step.VisibilityField),
          inPort: stepVisibilityIn,
        });
      }

      if (stepIdx === 0 && !stepHasVisibilityDependency) {
        const solutionToStep = solutionOut.link(stepIn);
        Object.assign(solutionToStep.getOptions() as any, {
          color: "#64748b",
          width: 2,
        });
        model.addLink(solutionToStep);
      }

      if (stepIdx > 0 && !stepHasVisibilityDependency) {
        const prevToCurrent = stepPorts[stepIdx - 1].outPort.link(stepIn);
        Object.assign(prevToCurrent.getOptions() as any, {
          color: "#0d6efd",
          width: 2,
        });
        model.addLink(prevToCurrent);
      }

      let groupOffsetY = 0;

      (step.inputGroups || []).forEach((group: any, groupIdx: number) => {
        const groupHasVisibilityDependency = !!group.VisibilityField;
        const groupNode = new SolutionFlowNodeModel({
          nodeKind: "group",
          title: group.name || `Group ${groupIdx + 1}`,
          subtitle: group.description || "",
          meta: `${group.inputs?.length || 0} input(s)`,
          color: "rgb(22, 163, 74)",
          flowSelection: {
            nodeKind: "group" as const,
            stepIndex: stepIdx,
            groupId: group.id,
          },
        });
        const groupX = stepX + LAYOUT.stepToGroupX;
        const groupY = stepY + groupOffsetY;
        groupNode.setPosition(groupX, groupY);
        const groupIn = groupNode.getPort("group-in") as any;
        const groupOut = groupNode.getPort("inputs-out") as any;
        const groupVisibilityIn = groupNode.getPort("visibility-in") as any;
        model.addNode(groupNode);

        if (group.id) {
          focusTargetRef.current[`step:${stepIdx}|group:${group.id}`] = {
            x: groupX,
            y: groupY,
            width: 240,
            height: 120,
          };
        }

        if (group.VisibilityField) {
          visibilityDependents.push({
            y: groupY,
            visibilityField: String(group.VisibilityField),
            inPort: groupVisibilityIn,
          });
        }

        if (!groupHasVisibilityDependency) {
          const stepToGroup = groupsOutPort.link(groupIn);
          Object.assign(stepToGroup.getOptions() as any, {
            color: "#16a34a",
            width: 2,
          });
          model.addLink(stepToGroup);
        }

        let previousSequentialInputOut: any = null;
        let isFirstNonVisibilityInput = true;

        (group.inputs || []).forEach((input: any, inputIdx: number) => {
          const inputHasVisibilityDependency = !!input.VisibilityField;
          const inputFocusKey = getInputFocusKey(
            stepIdx,
            group.id,
            input,
            inputIdx
          );
          const inputNode = new SolutionFlowNodeModel({
            nodeKind: "input",
            title: input.Label || input.Name || `Input ${inputIdx + 1}`,
            subtitle: input.Description || "",
            meta: input.Field ? `Field: ${input.Field}` : "",
            color: "rgb(148, 163, 184)",
            flowSelection: {
              nodeKind: "input" as const,
              stepIndex: stepIdx,
              groupId: group.id,
              inputIndex: inputIdx,
              inputFocusKey: inputFocusKey || undefined,
            },
          });
          const inputColumn = inputIdx % LAYOUT.maxInputsPerRow;
          const inputRow = Math.floor(inputIdx / LAYOUT.maxInputsPerRow);
          const inputX =
            groupX +
            LAYOUT.groupToInputX +
            inputColumn * LAYOUT.inputHorizontalGap;
          const inputY = groupY + inputRow * LAYOUT.inputVerticalGap;
          inputNode.setPosition(inputX, inputY);
          const inputIn = inputNode.getPort("input-in") as any;
          const inputOut = inputNode.getPort("input-out") as any;
          const inputVisibilityIn = inputNode.getPort("visibility-in") as any;
          const inputVisibilityOut = inputNode.getPort("visibility-out") as any;
          model.addNode(inputNode);

          if (inputFocusKey) {
            focusTargetRef.current[inputFocusKey] = {
              x: inputX,
              y: inputY,
              width: 240,
              height: 120,
            };
          }

          if (!inputHasVisibilityDependency) {
            if (isFirstNonVisibilityInput) {
              const groupToInput = groupOut.link(inputIn);
              Object.assign(groupToInput.getOptions() as any, {
                color: "#94a3b8",
                width: 2,
              });
              model.addLink(groupToInput);
              isFirstNonVisibilityInput = false;
            }

            if (previousSequentialInputOut) {
              const sequentialInputLink = previousSequentialInputOut.link(inputIn);
              Object.assign(sequentialInputLink.getOptions() as any, {
                color: "#64748b",
                width: 2,
              });
              model.addLink(sequentialInputLink);
            }

            previousSequentialInputOut = inputOut;
          } else {
            // Keep visibility-dependent inputs detached from structural chains.
            previousSequentialInputOut = null;
          }

          if (input.UUID) {
            inputLookup.set(`uuid:${input.UUID}`, {
              inPort: inputIn,
              outPort: inputOut,
            });
          }
          if (input.ComponentUuid && input.Field) {
            inputLookup.set(`field:${input.ComponentUuid}.${input.Field}`, {
              inPort: inputIn,
              outPort: inputOut,
            });
          }
          if (input.ComponentUuid && input.UUID) {
            inputLookup.set(`component:${input.ComponentUuid}.${input.UUID}`, {
              inPort: inputIn,
              outPort: inputOut,
            });
          }

          if (input.Field) {
            const fieldKey = String(input.Field);
            const existingSources = visibilitySourcesByField.get(fieldKey) || [];
            existingSources.push({ y: inputY, outPort: inputVisibilityOut });
            visibilitySourcesByField.set(fieldKey, existingSources);
          }

          if (input.VisibilityField) {
            visibilityDependents.push({
              y: inputY,
              visibilityField: String(input.VisibilityField),
              inPort: inputVisibilityIn,
            });
          }

          (input.MergedInputRefs || []).forEach((ref: any, refIdx: number) => {
            if (inputHasVisibilityDependency) return;
            const byField = ref.ComponentUuid && ref.Field
              ? inputLookup.get(`field:${ref.ComponentUuid}.${ref.Field}`)
              : null;
            const byUuid = ref.UUID ? inputLookup.get(`uuid:${ref.UUID}`) : null;
            const byComponentUuid = ref.ComponentUuid && ref.UUID
              ? inputLookup.get(`component:${ref.ComponentUuid}.${ref.UUID}`)
              : null;
            const sourceInput = byField || byUuid || byComponentUuid;

            if (!sourceInput || sourceInput.inPort === inputIn) return;

            const mergeLink = sourceInput.outPort.link(inputIn);
            Object.assign(mergeLink.getOptions() as any, {
              color: "#f59e0b",
              width: 1,
            });
            mergeLink.addLabel(`merge ${refIdx + 1}`);
            model.addLink(mergeLink);
          });
        });

        groupOffsetY += getGroupStackSpan(group);
      });

      nextStepY += stepRequiredSpan + LAYOUT.stepVerticalGap;
    });

    visibilityDependents.forEach((dependent) => {
      const candidates = visibilitySourcesByField.get(dependent.visibilityField) || [];
      if (!candidates.length) return;

      const source =
        [...candidates]
          .filter((candidate) => candidate.y < dependent.y)
          .sort((a, b) => b.y - a.y)[0] || candidates[0];

      if (!source?.outPort || !dependent.inPort) return;

      const visibilityLink = source.outPort.link(dependent.inPort);
      Object.assign(visibilityLink.getOptions() as any, {
        color: "#ec4899",
        width: 2,
      });
      visibilityLink.addLabel("visibility");
      model.addLink(visibilityLink);
    });

    return model;
  }, [solution?.Label, solution?.Reference, steps]);

  useLayoutEffect(() => {
    const diagramEngine = engine as any;
    const previousModel = diagramEngine?.getModel?.() || diagramEngine?.model;

    if (previousModel && previousModel !== model && !pendingInitialFitRef.current) {
      stopViewportAnimation();
      setViewport(
        model,
        previousModel.getOffsetX?.() || 0,
        previousModel.getOffsetY?.() || 0,
        previousModel.getZoomLevel?.() || 100
      );
    }

    let isCancelled = false;
    diagramEngine.setModel(model);
    const repaintResult = diagramEngine.repaintCanvas?.(true);

    Promise.resolve(repaintResult).then(() => {
      if (isCancelled) return;

      refreshLinkGeometry(diagramEngine);
      diagramEngine.repaintCanvas?.();
    });

    const repaintFrame = requestAnimationFrame(() => {
      if (isCancelled) return;

      refreshLinkGeometry(diagramEngine);
      diagramEngine.repaintCanvas?.();
    });

    return () => {
      isCancelled = true;
      cancelAnimationFrame(repaintFrame);
    };
  }, [engine, model, refreshLinkGeometry]);

  useEffect(() => {
    const diagramEngine = engine as any;
    const currentModel = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!currentModel) return;

    (currentModel.getNodes?.() || []).forEach((node: any) => {
      const selection = node?.options?.flowSelection;
      if (selection?.nodeKind !== "step") return;

      node.options.color =
        selection.stepIndex === activeStepIndex
          ? "rgb(108, 92, 231)"
          : "rgb(72, 71, 95)";
    });

    diagramEngine.repaintCanvas?.();
  }, [activeStepIndex, engine, model]);

  useEffect(() => {
    const diagramEngine = engine as any;
    const currentModel = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!currentModel) return;

    const resolvedFocusKey =
      (focusedInputNodeKey && focusTargetRef.current[focusedInputNodeKey]
        ? focusedInputNodeKey
        : null) ||
      (focusedInputGroupId && focusTargetRef.current[focusedInputGroupId]
        ? focusedInputGroupId
        : null);

    (currentModel.getNodes?.() || []).forEach((node: any) => {
      const selection = node?.options?.flowSelection;
      if (!selection) {
        node.setSelected?.(false);
        node.options.isFlowFocused = false;
        return;
      }

      const nodeFocusKey =
        selection.nodeKind === "input"
          ? selection.inputFocusKey || null
          : selection.nodeKind === "group" &&
            selection.groupId !== undefined &&
            selection.stepIndex !== undefined
          ? `step:${selection.stepIndex}|group:${selection.groupId}`
          : null;

      const isFlowFocused =
        !!resolvedFocusKey && nodeFocusKey === resolvedFocusKey;
      if (!isFlowFocused && node.isSelected?.()) {
        node.setSelected?.(false);
      }

      if (node.options.isFlowFocused !== isFlowFocused) {
        node.options.isFlowFocused = isFlowFocused;
        node.fireEvent?.({ isFlowFocused }, "flowFocusChanged");
      }
    });

    diagramEngine.repaintCanvas?.();
  }, [engine, model, focusedInputGroupId, focusedInputNodeKey]);

  const stopViewportAnimation = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const setViewport = (
    model: any,
    offsetX: number,
    offsetY: number,
    zoomLevel?: number
  ) => {
    if (typeof zoomLevel === "number") {
      model.setZoomLevel?.(zoomLevel);
    }

    if (typeof model.setOffset === "function") {
      model.setOffset(offsetX, offsetY);
    } else {
      model.setOffsetX?.(offsetX);
      model.setOffsetY?.(offsetY);
    }
  };

  const animateViewport = ({
    toOffsetX,
    toOffsetY,
    toZoom,
    duration = 280,
  }: {
    toOffsetX: number;
    toOffsetY: number;
    toZoom?: number;
    duration?: number;
  }) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!model) return;

    stopViewportAnimation();

    const fromOffsetX = model.getOffsetX?.() || 0;
    const fromOffsetY = model.getOffsetY?.() || 0;
    const fromZoom = model.getZoomLevel?.() || 100;
    const resolvedToZoom = typeof toZoom === "number" ? toZoom : fromZoom;

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const start = performance.now();

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = duration <= 0 ? 1 : Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);

      const nextOffsetX = fromOffsetX + (toOffsetX - fromOffsetX) * eased;
      const nextOffsetY = fromOffsetY + (toOffsetY - fromOffsetY) * eased;
      const nextZoom = fromZoom + (resolvedToZoom - fromZoom) * eased;

      setViewport(model, nextOffsetX, nextOffsetY, nextZoom);
      diagramEngine.repaintCanvas?.();

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(tick);
      } else {
        animationFrameRef.current = null;
      }
    };
    animationFrameRef.current = requestAnimationFrame(tick);
  };

  const getNodeSize = (node: any) => {
    const kind = node?.options?.nodeKind;
    if (kind === "solution" || kind === "step") return { width: 260, height: 120 };
    if (kind === "group" || kind === "input") return { width: 240, height: 120 };
    return { width: 240, height: 120 };
  };

  const getFitViewport = (margin = 28, targetModel?: any) => {
    const diagramEngine = engine as any;
    const model = targetModel || diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) return null;

    const nodes = model.getNodes?.() || [];
    if (!nodes.length) return null;

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    nodes.forEach((node: any) => {
      const x = node?.position?.x ?? 0;
      const y = node?.position?.y ?? 0;
      const { width, height } = getNodeSize(node);

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x + width);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y + height);
    });

    const boundsWidth = Math.max(1, maxX - minX);
    const boundsHeight = Math.max(1, maxY - minY);
    const availableWidth = Math.max(1, container.clientWidth - margin * 2);
    const availableHeight = Math.max(1, container.clientHeight - margin * 2);
    const fitZoom = Math.min(
      100,
      (availableWidth / boundsWidth) * 100,
      (availableHeight / boundsHeight) * 100
    );

    const centerX = minX + boundsWidth / 2;
    const centerY = minY + boundsHeight / 2;
    const offsetX = container.clientWidth / 2 - (centerX * fitZoom) / 100;
    const offsetY = container.clientHeight / 2 - (centerY * fitZoom) / 100;

    return {
      zoom: fitZoom,
      offsetX,
      offsetY,
    };
  };

  useEffect(() => {
    const targetKey =
      (focusedInputNodeKey && focusTargetRef.current[focusedInputNodeKey]
        ? focusedInputNodeKey
        : null) ||
      (focusedInputGroupId && focusTargetRef.current[focusedInputGroupId]
        ? focusedInputGroupId
        : null) ||
      (typeof activeStepIndex === "number" &&
      focusTargetRef.current[`step:${activeStepIndex}`]
        ? `step:${activeStepIndex}`
        : null);
    if (!targetKey) return;

    const target = focusTargetRef.current[targetKey];
    if (!target) return;

    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) return;

    const zoom = model.getZoomLevel?.() || 100;
    const offsetX = model.getOffsetX?.() || 0;
    const offsetY = model.getOffsetY?.() || 0;
    const isFocusedInput = !!focusedInputNodeKey && targetKey === focusedInputNodeKey;
    const targetZoom = isFocusedInput && zoom < 50 ? 50 : zoom;
    const zoomScale = targetZoom / 100;
    const targetLeft = target.x * zoomScale + offsetX;
    const targetRight = (target.x + target.width) * zoomScale + offsetX;
    const targetTop = target.y * zoomScale + offsetY;
    const targetBottom = (target.y + target.height) * zoomScale + offsetY;
    const viewLeft = FOCUS_VIEW_MARGIN;
    const viewRight = container.clientWidth - FOCUS_VIEW_MARGIN;
    const viewTop = FOCUS_VIEW_MARGIN;
    const viewBottom = container.clientHeight - FOCUS_VIEW_MARGIN;

    let nextOffsetX = offsetX;
    let nextOffsetY = offsetY;

    if (targetLeft < viewLeft) {
      nextOffsetX += viewLeft - targetLeft;
    } else if (targetRight > viewRight) {
      nextOffsetX -= targetRight - viewRight;
    }

    if (targetTop < viewTop) {
      nextOffsetY += viewTop - targetTop;
    } else if (targetBottom > viewBottom) {
      nextOffsetY -= targetBottom - viewBottom;
    }

    const shouldChangeZoom = targetZoom !== zoom;
    if (nextOffsetX === offsetX && nextOffsetY === offsetY && !shouldChangeZoom) return;

    animateViewport({
      toOffsetX: nextOffsetX,
      toOffsetY: nextOffsetY,
      toZoom: shouldChangeZoom ? targetZoom : undefined,
      duration: 240,
    });
  }, [activeStepIndex, engine, focusedInputGroupId, focusedInputNodeKey]);

  const zoomToFitDiagram = (animate = true, targetModel?: any) => {
    const fitTarget = getFitViewport(28, targetModel);
    if (!fitTarget) return;

    if (!animate) {
      const diagramEngine = engine as any;
      const model = targetModel || diagramEngine?.getModel?.() || diagramEngine?.model;
      if (!model) return;

      stopViewportAnimation();
      setViewport(model, fitTarget.offsetX, fitTarget.offsetY, fitTarget.zoom);
      pendingInitialFitRef.current = false;
      diagramEngine.repaintCanvas?.();
      return;
    }

    animateViewport({
      toOffsetX: fitTarget.offsetX,
      toOffsetY: fitTarget.offsetY,
      toZoom: fitTarget.zoom,
      duration: 320,
    });
    pendingInitialFitRef.current = false;
  };

  useLayoutEffect(() => {
    if (!steps.length) {
      previousStructureSignatureRef.current = structureSignature;
      return;
    }

    const isInitialFit = previousStructureSignatureRef.current === null;

    if (
      !isInitialFit &&
      previousStructureSignatureRef.current === structureSignature
    ) {
      return;
    }

    previousStructureSignatureRef.current = structureSignature;

    const frames: number[] = [];
    const scheduleFrame = (callback: FrameRequestCallback) => {
      const frame = requestAnimationFrame(callback);
      frames.push(frame);
      return frame;
    };

    const fitWhenReady = (attempt = 0) => {
      refreshLinkGeometry(engine as any);

      const container = diagramContainerRef.current;
      if (container?.clientWidth && container?.clientHeight) {
        zoomToFitDiagram(!isInitialFit, model);
        return;
      }

      if (attempt < 6) {
        scheduleFrame(() => fitWhenReady(attempt + 1));
      }
    };

    if (isInitialFit) {
      fitWhenReady();
      return () => {
        frames.forEach((frame) => cancelAnimationFrame(frame));
      };
    }

    const firstFrame = requestAnimationFrame(() => {
      refreshLinkGeometry(engine as any);
      frames.push(
        requestAnimationFrame(() => {
          refreshLinkGeometry(engine as any);
          scheduleFrame(() => fitWhenReady());
        })
      );
    });
    frames.push(firstFrame);

    return () => {
      frames.forEach((frame) => cancelAnimationFrame(frame));
    };
  }, [engine, model, refreshLinkGeometry, steps.length, structureSignature]);

  const zoomKeepingPoint = (
    clientX: number,
    clientY: number,
    targetZoom: number,
    duration = 0
  ) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const container = diagramContainerRef.current;
    if (!model || !container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const pointerX = clientX - rect.left;
    const pointerY = clientY - rect.top;

    const zoomBefore = model.getZoomLevel?.() || 100;
    const offsetXBefore = model.getOffsetX?.() || 0;
    const offsetYBefore = model.getOffsetY?.() || 0;

    const modelX = ((pointerX - offsetXBefore) / zoomBefore) * 100;
    const modelY = ((pointerY - offsetYBefore) / zoomBefore) * 100;

    const nextOffsetX = pointerX - (modelX * targetZoom) / 100;
    const nextOffsetY = pointerY - (modelY * targetZoom) / 100;

    if (duration > 0) {
      stopViewportAnimation();

      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      const start = performance.now();

      const tick = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / duration);
        const eased = easeOutCubic(progress);
        const nextZoom = zoomBefore + (targetZoom - zoomBefore) * eased;

        setViewport(
          model,
          pointerX - (modelX * nextZoom) / 100,
          pointerY - (modelY * nextZoom) / 100,
          nextZoom
        );
        diagramEngine.repaintCanvas?.();

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(tick);
        } else {
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(tick);
      return;
    }

    stopViewportAnimation();
    setViewport(model, nextOffsetX, nextOffsetY, targetZoom);
    diagramEngine.repaintCanvas?.();
  };

  diagramNodeClickRef.current = (selection: any) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    const zoomBefore = model?.getZoomLevel?.() || 100;
    if (
      zoomBefore < 50 &&
      typeof selection?.clientX === "number" &&
      typeof selection?.clientY === "number"
    ) {
      zoomKeepingPoint(selection.clientX, selection.clientY, 50, 220);
    }

    onNodeClickRef.current?.(selection);
  };

  const handleDiagramWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!model) return;

    const zoomBefore = model.getZoomLevel?.() || 100;
    const zoomDelta = event.deltaY > 0 ? -8 : 8;
    const nextZoom = Math.max(20, Math.min(180, zoomBefore + zoomDelta));

    zoomKeepingPoint(event.clientX, event.clientY, nextZoom);
  };

  const handleWheelZoom = useCallback((event: WheelEvent) => {
    const container = diagramContainerRef.current;
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!container || !model) return;

    const rect = container.getBoundingClientRect();
    const isInsideDiagram =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isInsideDiagram) return;

    event.preventDefault();
    event.stopPropagation();

    const zoomBefore = model.getZoomLevel?.() || 100;
    const zoomDelta = event.deltaY > 0 ? -8 : 8;
    const nextZoom = Math.max(20, Math.min(180, zoomBefore + zoomDelta));

    zoomKeepingPoint(event.clientX, event.clientY, nextZoom);
  }, [engine]);

  const handleDiagramDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const diagramEngine = engine as any;
    const model = diagramEngine?.getModel?.() || diagramEngine?.model;
    if (!model) return;

    const target = e.target as HTMLElement | null;
    if (!target?.closest('[data-solution-flow-node="true"]')) {
      zoomToFitDiagram();
      return;
    }

    const zoomBefore = model.getZoomLevel?.() || 100;
    zoomKeepingPoint(e.clientX, e.clientY, zoomBefore + 50, 260);
  };

  const handleToggleFullscreen = async () => {
    const element = diagramContainerRef.current;
    if (!element) return;

    if (document.fullscreenElement === element) {
      await document.exitFullscreen();
      return;
    }

    await element.requestFullscreen();
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const nextIsFullscreen = document.fullscreenElement === diagramContainerRef.current;
      setIsFullscreen(nextIsFullscreen);
    };

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const container = diagramContainerRef.current;

    const wheelListenerOptions = { capture: true, passive: false };

    document.addEventListener("wheel", handleWheelZoom, wheelListenerOptions);
    return () => {
      document.removeEventListener("wheel", handleWheelZoom, wheelListenerOptions);
    };
  }, [handleWheelZoom]);

  useEffect(() => {
    return () => {
      stopViewportAnimation();
    };
  }, []);

  return (
    <div className="p-4 h-100 d-flex flex-column">
      <div className="fw-bold fs-2 mb-2">Solution Flow Overview</div>
      <div className="text-secondary text-sm mb-3">
        Linked diagram of solution, steps, input groups, and inputs.
      </div>
      <div className="small text-secondary mb-2">
        <span className="me-3">Blue/Grey: step flow</span>
        <span className="me-3">Green: step to input group</span>
        <span className="me-3">Slate: group to input</span>
        <span className="me-3">Amber: merged input links</span>
        <span>Pink: visibility rules</span>
      </div>
      {steps.length === 0 ? (
        <div className="text-secondary">No steps found in this solution yet.</div>
      ) : (
        <div
          ref={diagramContainerRef}
          className="border rounded-3 bg-solution"
          style={{
            height: isFullscreen ? "100vh" : "100%",
            minHeight: isFullscreen ? undefined : 420,
            position: "relative",
            overscrollBehavior: "contain",
            touchAction: "none",
          }}
          onDoubleClick={handleDiagramDoubleClick}
          onWheel={handleDiagramWheel}
        >
          <button
            type="button"
            className="btn btn-sm btn-light border"
            style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
            onClick={handleToggleFullscreen}
          >
            {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          </button>
          <CanvasWidget
            engine={engine}
            className="h-100 diagram-container"
          />
        </div>
      )}
    </div>
  );
};

export default SolutionFlowOverview;
